import App from "@bloglovin/inspire-engine/App";
import OptionExpression from "@bloglovin/inspire-engine/Helpers/OptionExpression.js";
import { Context } from "%/dynamic-component-editor/classes/context-v2";
import { resolveContextEngineAppName } from "%/dynamic-component-editor/classes/platform-standard-entities";
import { withExpressionGlobals } from "%/dynamic-component-editor/helpers/expression-global-values";

export const PAGE_ENTITY_TYPE = "Page";
export const PAGE_FIELD_SLUG = "slug";
export const PAGE_FIELD_AVAILABLE = "available";

const PLACEHOLDER = /\{([^}]+)\}/g;

const stripAppPrefix = type => String(type || "").split(".").at(-1);
const unwrap = maybe => (typeof maybe === "function" ? maybe() : maybe) ?? {};

export function isPageExpression(page) {
  return OptionExpression.isToken(page);
}

export function contextNamesInPageExpression(page) {
  const names = [];
  const walk = node => {
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (!node || typeof node !== "object") {
      return;
    }
    if ((node.source === "context" || node.source === "contextField") && typeof node.context === "string" && !names.includes(node.context)) {
      names.push(node.context);
    }
    Object.values(node).forEach(walk);
  };
  walk(page?.expr?.ast);
  return names;
}

function withUrlVars(contexts) {
  const normalised = {};
  for (const [name, entry] of Object.entries(contexts || {})) {
    if (entry && typeof entry === "object" && !entry.entityPkUrlVars && entry.entityPkVars) {
      normalised[name] = { ...entry, entityPkUrlVars: entry.entityPkVars };
    } else {
      normalised[name] = entry;
    }
  }
  return normalised;
}

export function resolvePageLinkValue(action, urlValue, { contexts = {}, readEntityField, quiet = false } = {}) {
  if (!isPageExpression(action?.page)) {
    return urlValue;
  }
  const fail = reason => {
    if (!quiet) {
      // eslint-disable-next-line no-console
      console.error(`[click-action] ${reason}, so this link goes nowhere`, { page: action.page });
    }
    return null;
  };
  const scope = withExpressionGlobals(withUrlVars(contexts));
  const id = OptionExpression.resolve(action.page, { contexts: scope, readEntityField });
  if (id === undefined) {
    const missing = contextNamesInPageExpression(action.page).filter(name => !scope[name]);
    if (missing.length) {
      return fail(`no "${missing.join("\", \"")}" context is in scope here to say which page to open`);
    }
    return fail("the page expression produced no page");
  }
  const pk = { id: Number(id) };
  const available = readEntityField(PAGE_ENTITY_TYPE, pk, PAGE_FIELD_AVAILABLE, null);
  if (available === undefined) {
    return null;
  }
  if (!available) {
    return fail(`page ${id} no longer exists or is not a page`);
  }
  const slug = readEntityField(PAGE_ENTITY_TYPE, pk, PAGE_FIELD_SLUG, null);
  if (slug === undefined) {
    return null;
  }
  const needs = [...String(slug).matchAll(PLACEHOLDER)].map(match => match[1]);
  if (needs.length) {
    return fail(`page ${id} needs URL values (${needs.join(", ")}), and a page chosen from context must be a fixed page`);
  }
  return String(slug);
}

function readFromApp(entityType, pk, fieldName, appName) {
  if (pk && Object.prototype.hasOwnProperty.call(pk, fieldName)) {
    return pk[fieldName];
  }
  const app = App.get(resolveContextEngineAppName(appName));
  const type = stripAppPrefix(entityType);
  const hydrated = app.hasEntity(type, pk) && app.getEntityField(type, pk, "isHydrated") === true;
  const value = app.getEntityField(type, pk, fieldName);
  return hydrated ? value : undefined;
}

export function pageLinkValueFor(action, urlValue, scopedContexts, { quiet = false } = {}) {
  if (!isPageExpression(action?.page)) {
    return urlValue;
  }
  return resolvePageLinkValue(action, urlValue, {
    contexts: { ...Context.allContexts, ...unwrap(scopedContexts) },
    readEntityField: readFromApp,
    quiet,
  });
}

export function isBuilderQuiet(vm) {
  return Boolean(typeof vm?.inBuilderEditScope === "function" ? vm.inBuilderEditScope() : vm?.inBuilderEditScope);
}

export function describePageExpression(page) {
  const names = contextNamesInPageExpression(page);
  return names.length ? `the page in ${names.join(", ")}` : "the page its expression picks";
}
